import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { 
  Receipt, TrendingDown, TrendingUp, DollarSign, 
  Download, Calendar, FileText, AlertCircle 
} from 'lucide-react';

const VendorTaxDashboard = () => {
  const { toast } = useToast();
  const { user, apiCall } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('current_fy');

  useEffect(() => {
    fetchTaxSummary();
  }, [selectedPeriod]);

  const fetchTaxSummary = async () => {
    setLoading(true);
    try {
      const response = await apiCall(`/api/tax-management/vendor-summary/${user.id}`);
      if (response.success) {
        setSummary(response.summary);
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.message, 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadStatement = async () => {
    toast({ 
      title: 'Download Started', 
      description: 'Your tax statement is being prepared...' 
    });
    // TODO: Implement PDF download
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Loading tax summary...</div>
        </div>
      </DashboardLayout>
    );
  }

  const stats = [
    {
      title: 'Total Gross Sales',
      value: `₹${summary?.total_gross_sales?.toLocaleString('en-IN') || '0'}`,
      icon: DollarSign,
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      title: 'TDS Deducted',
      value: `₹${summary?.total_tds_deducted?.toLocaleString('en-IN') || '0'}`,
      icon: TrendingDown,
      color: 'text-red-600',
      bg: 'bg-red-50',
      subtitle: 'Section 194-O'
    },
    {
      title: 'TCS Collected',
      value: `₹${summary?.total_tcs_collected?.toLocaleString('en-IN') || '0'}`,
      icon: Receipt,
      color: 'text-orange-600',
      bg: 'bg-orange-50',
      subtitle: 'GST TCS @ 1%'
    },
    {
      title: 'Net Payout',
      value: `₹${summary?.total_net_payout?.toLocaleString('en-IN') || '0'}`,
      icon: TrendingUp,
      color: 'text-green-600',
      bg: 'bg-green-50'
    }
  ];

  return (
    <DashboardLayout>
      <div className="p-2 sm:p-4 md:p-6 space-y-4 md:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h1 className="text-xl md:text-3xl font-bold text-gray-900">Tax Dashboard</h1>
            <p className="text-gray-600 text-sm mt-1">
              View your tax deductions and statements
            </p>
          </div>
          <div className="flex gap-2 sm:gap-3 flex-wrap">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="current_fy">Current FY ({summary?.financial_year})</option>
              <option value="last_fy">Last FY</option>
              <option value="current_quarter">Current Quarter</option>
              <option value="last_quarter">Last Quarter</option>
            </select>
            <Button onClick={downloadStatement} size="sm">
              <Download className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Download</span> Statement
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          {stats.map((stat, index) => (
            <Card key={stat.id || stat.name || `${stat.title || "stat"}-${index}`}>
              <CardContent className="p-3 md:p-6">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs md:text-sm font-medium text-gray-600 truncate">{stat.title}</p>
                    <p className="text-lg md:text-2xl font-bold mt-1 md:mt-2">{stat.value}</p>
                    {stat.subtitle && (
                      <p className="text-xs text-gray-500 mt-1 truncate">{stat.subtitle}</p>
                    )}
                  </div>
                  <div className={`${stat.bg} p-2 md:p-3 rounded-lg flex-shrink-0`}>
                    <stat.icon className={`w-4 h-4 md:w-6 md:h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tax Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* TDS Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <TrendingDown className="w-5 h-5 mr-2 text-red-600" />
                TDS Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium">Total Transactions</span>
                  <span className="font-bold">{summary?.total_transactions || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium">TDS Rate Applied</span>
                  <span className="font-bold">0.1%</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg border border-red-200">
                  <span className="text-sm font-medium text-red-700">Total TDS Deducted</span>
                  <span className="font-bold text-red-700">
                    ₹{summary?.total_tds_deducted?.toLocaleString('en-IN') || '0'}
                  </span>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                    <div className="text-sm text-blue-700">
                      <strong>Note:</strong> TDS can be claimed as tax credit in your ITR filing. 
                      Download Form 26AS from Income Tax portal.
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* TCS Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Receipt className="w-5 h-5 mr-2 text-orange-600" />
                TCS Breakdown (GST)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium">CGST (0.5%)</span>
                  <span className="font-bold">
                    ₹{summary?.tcs_cgst?.toLocaleString('en-IN') || '0'}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium">SGST (0.5%)</span>
                  <span className="font-bold">
                    ₹{summary?.tcs_sgst?.toLocaleString('en-IN') || '0'}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium">IGST (1%)</span>
                  <span className="font-bold">
                    ₹{summary?.tcs_igst?.toLocaleString('en-IN') || '0'}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <span className="text-sm font-medium text-orange-700">Total TCS Collected</span>
                  <span className="font-bold text-orange-700">
                    ₹{summary?.total_tcs_collected?.toLocaleString('en-IN') || '0'}
                  </span>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                    <div className="text-sm text-blue-700">
                      <strong>Note:</strong> TCS appears in your electronic cash ledger on GST portal. 
                      Claim it as input tax credit in GSTR-3B.
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Commission Details */}
        <Card>
          <CardHeader>
            <CardTitle>Commission & Other Deductions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Total Commissions</p>
                <p className="text-2xl font-bold">
                  ₹{summary?.total_commissions?.toLocaleString('en-IN') || '0'}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Commission GST (18%)</p>
                <p className="text-2xl font-bold">
                  ₹{summary?.total_commission_gst?.toLocaleString('en-IN') || '0'}
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-700 mb-1 font-medium">Net Amount Received</p>
                <p className="text-2xl font-bold text-green-700">
                  ₹{summary?.total_net_payout?.toLocaleString('en-IN') || '0'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Tax Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.entries && summary.entries.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Date</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Order ID</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">Gross Amount</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">TDS</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">TCS</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">Net Payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.entries.slice(0, 10).map((entry, index) => (
                      <tr key={entry.id || entry.name || `${entry.title || "entry"}-${index}`} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-sm">
                          {new Date(entry.transaction_date).toLocaleDateString('en-IN')}
                        </td>
                        <td className="p-3 text-sm font-mono">{entry.order_id}</td>
                        <td className="p-3 text-sm text-right">
                          ₹{entry.gross_amount?.toLocaleString('en-IN')}
                        </td>
                        <td className="p-3 text-sm text-right text-red-600">
                          -₹{entry.tds_amount?.toLocaleString('en-IN')}
                        </td>
                        <td className="p-3 text-sm text-right text-orange-600">
                          -₹{entry.tcs_amount?.toLocaleString('en-IN')}
                        </td>
                        <td className="p-3 text-sm text-right font-semibold text-green-600">
                          ₹{entry.net_payout?.toLocaleString('en-IN')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No tax transactions found for this period</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Section */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-start">
              <AlertCircle className="w-6 h-6 text-blue-600 mr-3 mt-1" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Need Help with Taxes?</h3>
                <p className="text-sm text-blue-700 mb-3">
                  Understanding e-commerce taxation can be complex. Here are some resources:
                </p>
                <ul className="space-y-2 text-sm text-blue-700">
                  <li>• <strong>TDS (Section 194-O):</strong> Deducted at 0.1% on gross sales</li>
                  <li>• <strong>TCS (GST):</strong> Collected at 1% on net taxable value</li>
                  <li>• <strong>Tax Credits:</strong> Both can be claimed in your tax returns</li>
                  <li>• <strong>Quarterly Statement:</strong> Download for ITR filing</li>
                </ul>
                <Button variant="outline" size="sm" className="mt-4">
                  View Tax Guide
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default VendorTaxDashboard;
